import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MainLayout } from '@/shared/components/layout'
import { useAuth } from '@/shared/contexts/AuthContext'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { getUnitMembers, removeUnitMember, updateUnitMemberRole } from './unitMembersService'
import { getUnitInvitations, inviteUserToUnit, cancelInvitation } from './unitInvitationsService'

import type { Role } from '@/lib/permissions/types'

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  manager: 'Gerente',
  operator: 'Operador',
  viewer: 'Visualizador',
}

export default function UnitMembers() {
  const { unitId } = useParams<{ unitId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { tu } = useTranslation()

  const [members, setMembers] = useState<any[]>([])
  const [invitations, setInvitations] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('viewer')
  const [inviting, setInviting] = useState(false)

  const load = async () => {
    if (!unitId) return
    setLoading(true)
    setError(null)
    try {
      const [m, inv] = await Promise.all([
        getUnitMembers(unitId),
        getUnitInvitations(unitId),
      ])
      setMembers(m)
      setInvitations(inv.filter((i: any) => i.status === 'pending'))
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [unitId])

  const flash = (msg: string, type: 'success' | 'error') => {
    if (type === 'success') { setSuccess(msg); setTimeout(() => setSuccess(null), 4000) }
    else { setError(msg); setTimeout(() => setError(null), 5000) }
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!unitId || !inviteEmail.trim()) return
    setInviting(true)
    try {
      await inviteUserToUnit(unitId, inviteEmail.trim(), inviteRole)
      setInviteEmail('')
      flash('Convite enviado com sucesso!', 'success')
      await load()
    } catch (e: any) {
      flash(e.message || 'Erro ao enviar convite', 'error')
    } finally {
      setInviting(false)
    }
  }

  const handleCancelInvite = async (id: string) => {
    try {
      await cancelInvitation(id)
      flash('Convite cancelado.', 'success')
      await load()
    } catch (e: any) {
      flash(e.message || 'Erro ao cancelar convite', 'error')
    }
  }

  const handleRoleChange = async (memberId: string, role: Role) => {
    try {
      await updateUnitMemberRole(memberId, role)
      flash('Papel atualizado.', 'success')
      await load()
    } catch (e: any) {
      flash(e.message || 'Erro ao atualizar papel', 'error')
    }
  }

  const handleRemove = async (memberId: string, name: string) => {
    if (!confirm(`Remover ${name} do time?`)) return
    try {
      await removeUnitMember(memberId)
      flash(`${name} removido do time.`, 'success')
      await load()
    } catch (e: any) {
      flash(e.message || 'Erro ao remover membro', 'error')
    }
  }

  return (
    <MainLayout userName={user?.email} userId={user?.id}>
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/units')}
            className="rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Gerenciar time</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Membros e convites pendentes</p>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800/30 px-4 py-3 text-sm text-green-700 dark:text-green-400">
            {success}
          </div>
        )}

        {/* Invite form */}
        <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <h2 className="mb-4 text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Convidar usuário</h2>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              placeholder="email@exemplo.com"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <select
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value as Role)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="viewer">Visualizador</option>
              <option value="manager">Gerente</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={inviting}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60 transition-colors"
            >
              {inviting ? 'Enviando...' : 'Convidar'}
            </button>
          </form>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
          </div>
        ) : (
          <>
            {/* Members list */}
            <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Membros · {members.length}
                </h2>
              </div>
              {members.length === 0 ? (
                <p className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500">Nenhum membro encontrado.</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {members.map((m) => {
                    const isSelf = m.user_id === user?.id
                    const name = m.full_name || m.email || m.user_id
                    return (
                      <li key={m.id} className="flex items-center gap-3 px-5 py-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-semibold">
                          {(name[0] || '?').toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                            {name} {isSelf && <span className="text-xs text-gray-400">(você)</span>}
                          </p>
                          {m.email && m.full_name && (
                            <p className="truncate text-xs text-gray-400">{m.email}</p>
                          )}
                        </div>
                        <select
                          value={m.role}
                          disabled={isSelf}
                          onChange={e => handleRoleChange(m.id, e.target.value as Role)}
                          className="rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-green-500"
                        >
                          <option value="admin">Admin</option>
                          <option value="manager">Gerente</option>
                          <option value="viewer">Visualizador</option>
                        </select>
                        {!isSelf && (
                          <button
                            onClick={() => handleRemove(m.id, name)}
                            className="ml-1 rounded-md p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title="Remover membro"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {/* Pending invitations */}
            {invitations.length > 0 && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                    Convites pendentes · {invitations.length}
                  </h2>
                </div>
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {invitations.map((inv) => (
                    <li key={inv.id} className="flex items-center gap-3 px-5 py-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-400 dark:text-blue-500">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{inv.email}</p>
                        <p className="text-xs text-gray-400">{ROLE_LABELS[inv.role as Role] || inv.role} · {tu('record.team.awaitingResponse')}</p>
                      </div>
                      <button
                        onClick={() => handleCancelInvite(inv.id)}
                        className="rounded-md p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Cancelar convite"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  )
}
